import { PlayerProfile, CLASSES } from "./PlayerProfile.js";
import { logger } from "./Logger.js";

export class EconomySystem {
  constructor(config, persistedState = null) {
    this.config = config;

    // Estado inicial con soporte para Estamina
    const base = persistedState ?? {
      credits: 1000,
      badges: 10,
      pityCounter: 0,
      dailyPulls: 0,
      lastPullResetDate: this._today(),
      roster: {},
      stamina: 5, // Capacidad máxima inicial
      last_stamina_update: Date.now(),
      last_burnout_update: Date.now(),
      firstGuard: true,
      studyPoints: 0,
      caseHistory: {},
      playerXp: 0,
      playerLevel: 1,
      firstPullDone: false,
      rpgProfile: {}, // New RPG state container
      burnout: 0,
      activeMentor: null,
      secondaryMentor: null,
      precisionStreak: 0,
      excellenceBadges: 0,
      unlockedAchievements: [], // IDs of achievements unlocked
      dailyGuards: 0,
      lifetimeCleanCases: 0,
      perfectStabilityGuards: 0,
      lastGuardDate: this._today(),
      licenses: 0, // Nueva moneda de progresión
      discoveredPearls: {}, // { nodeId: { topic, pearl, rarity, seenAt } }
      adaptiveState: null, // Container for AdaptiveSystem persistence
      dayNumber: 1,
      guardsToday: 0,
      dayLog: [],
      storyBeatsSeen: []
    };

    this.rpg = new PlayerProfile(base.rpgProfile || {});

    Object.assign(this, base);
    if (!this.storyBeatsSeen) this.storyBeatsSeen = [];
    if (!this.discoveredPearls) this.discoveredPearls = {};
    if (!this.caseHistory) this.caseHistory = {};
    if (!this.dayLog) this.dayLog = [];
    if (typeof this.dayNumber !== "number") this.dayNumber = 1;
    if (typeof this.guardsToday !== "number") this.guardsToday = 0;
    if (typeof this.studyPoints !== "number") this.studyPoints = 0;
    if (typeof this.firstGuard !== "boolean") this.firstGuard = true;
    if (typeof this.firstPullDone !== "boolean") this.firstPullDone = false;
    if (typeof this.playerXp !== "number") this.playerXp = 0;
    if (typeof this.playerLevel !== "number") this.playerLevel = 1;
    if (typeof this.burnout !== "number") this.burnout = 0;
    if (typeof this.licenses !== "number") this.licenses = 0;
    if (!Array.isArray(this.unlockedAchievements)) this.unlockedAchievements = [];
    if (typeof this.dailyGuards !== "number") this.dailyGuards = 0;
    if (typeof this.lifetimeCleanCases !== "number") this.lifetimeCleanCases = 0;
    if (typeof this.perfectStabilityGuards !== "number") this.perfectStabilityGuards = 0;
    // Achievement-derived permanent bonuses
    if (typeof this.maxStamina !== "number") this.maxStamina = 5;
    if (typeof this.xpBonusMult !== "number") this.xpBonusMult = 0;
    if (typeof this.burnoutResist !== "number") this.burnoutResist = 0;
    const levelState = this._deriveLevel(this.playerXp);
    this.playerLevel = Math.max(this.playerLevel, levelState.level);
    this.state = this;
    this.recalculateStamina(); // Calcular recuperación al cargar
  }

  // Recuperación pasiva: 1 punto cada hora (3600000 ms)
  recalculateStamina() {
    const now = Date.now();
    
    // Stamina recovery: preserve fractional progress
    const msSinceStamina = now - this.last_stamina_update;
    const recoveryRateMs = 15 * 60 * 1000; // 15 minutos por punto de estamina
    if (msSinceStamina >= recoveryRateMs) {
      const pointsToRecover = Math.floor(msSinceStamina / recoveryRateMs);
      if (pointsToRecover > 0) {
        this.stamina = Math.min(this.maxStamina, this.stamina + pointsToRecover);
        this.last_stamina_update += pointsToRecover * recoveryRateMs;
      }
    }

    // Burnout Decay: -2% per hour in background
    if (!this.last_burnout_update) this.last_burnout_update = now;
    const msSinceBurnout = now - this.last_burnout_update;
    const hoursElapsed = msSinceBurnout / (3600 * 1000);
    if (hoursElapsed > 0.05) { // more than 3 minutes to avoid micro-updates
      const decayAmount = hoursElapsed * 2;
      this.addBurnout(-decayAmount);
      this.last_burnout_update = now;
    }
  } 
  
  // Consumo de energía al iniciar guardia con protección contra doble clic
  consumeStamina() {
    this.recalculateStamina();
    
    // Throttle: prevent consumption if last update was < 1s ago (debounce)
    const now = Date.now();
    if (this._last_consume && (now - this._last_consume < 1000)) {
        logger.warn("[Economy] Bloqueando consumo duplicado por debounce.");
        return false;
    }

    if (this.stamina > 0) {
      this.stamina = Math.max(0, this.stamina - 1);
      this.last_stamina_update = now;
      this._last_consume = now;
      return true;
    }
    return false;
  }


  syncDailyReset() {
    const current = this._today();
    if (this.lastPullResetDate !== current) {
      this.dailyPulls = 0;
      this.lastPullResetDate = current;
    }
    if (this.lastGuardDate !== current) {
      this.dailyGuards = 0;
      this.lastGuardDate = current;
    }
  }

  addCredits(amount) {
    this.credits += amount;
  }

  /**
   * Adds burnout, applying any permanent resistance earned via achievements.
   * Reductions (negative amounts) bypass resistance.
   * @returns {number} The new burnout value (0-100).
   */
  addBurnout(amount) {
    const delta = amount > 0 ? amount * (1 - this.burnoutResist) : amount;
    this.burnout = Math.min(100, Math.max(0, (this.burnout || 0) + delta));
    return this.burnout;
  }

  addPlayerXp(amount) {
    let xpGain = (typeof amount === 'number' && Number.isFinite(amount)) ? amount : 0;
    // Apply permanent XP bonus earned from achievements (e.g. xp_bonus rewards)
    if (this.xpBonusMult > 0) xpGain = Math.round(xpGain * (1 + this.xpBonusMult));
    this.playerXp += xpGain;
    const previousLevel = this.playerLevel;
    const levelState = this._deriveLevel(this.playerXp);
    this.playerLevel = levelState.level;
    const levelUps = Math.max(0, this.playerLevel - previousLevel);
    this._updateLevelLogic(levelUps);
    return {
      levelUps: Math.max(0, this.playerLevel - previousLevel),
      level: levelState.level,
      xpIntoLevel: levelState.xpIntoLevel,
      xpForNextLevel: levelState.xpForNextLevel
    };
  }

  // --- Wrapper for internal level state ---
  _updateLevelLogic(levelUps) {
    if (levelUps > 0 && this.rpg) {
      this.rpg.gainLevel(levelUps);
    }
  }

  getLevelState() {
    const levelState = this._deriveLevel(this.playerXp);
    return {
      level: levelState.level,
      xpIntoLevel: levelState.xpIntoLevel,
      xpForNextLevel: levelState.xpForNextLevel,
      progress: levelState.xpForNextLevel === 0
        ? 1
        : levelState.xpIntoLevel / levelState.xpForNextLevel
    };
  }

  /**
   * Realiza N pulls con descuento del 10% para packs de 10.
   * @param {number} count - Cantidad de pulls (1 o 10).
   * @returns {Array|null} Array de resultados o null si falló.
   */
  pull(count = 1) {
    this.syncDailyReset();
    const costPerPull = 100;
    const totalCost = count >= 10 ? Math.floor(count * costPerPull * 0.9) : count * costPerPull;

    if (this.credits < totalCost) return { success: false, reason: "No tienes suficientes créditos para realizar este reclutamiento." };
    
    const dailyCap = this.config?.anti_inflation?.daily_pull_cap || 10;
    if (this.dailyPulls + count > dailyCap) return { success: false, reason: "Has alcanzado el límite diario de reclutamientos." };

    this.credits -= totalCost;
    const results = [];

    for (let i = 0; i < count; i++) {
      this.pityCounter++;
      this.dailyPulls++;

      let rarity = this._determineRarity();
      if (!this.firstPullDone) {
        this.firstPullDone = true;
        rarity = Math.random() > 0.5 ? "SSR" : "SR";
      }
      if (rarity === "SSR") {
        logger.debug(`[Economy] SSR Pull at pity ${this.pityCounter}`);
        this.pityCounter = 0;
      }

      const mentors = window.MENTORS_DB || [];
      const pool = mentors.filter(m => m.rarity === rarity);
      const mentorData = pool.length > 0
        ? pool[Math.floor(Math.random() * pool.length)]
        : { id: `residente_${rarity.toLowerCase()}_${Math.floor(Math.random() * 1000)}`, name: `Residente ${rarity}`, rarity };

      const item = { id: mentorData.id, rarity, name: mentorData.name, icon: mentorData.icon, title: mentorData.title, type: "mentor", timestamp: Date.now() };
      const addResult = this._addToRoster(item);
      results.push({ 
        success: true, 
        item, 
        pityCounter: this.pityCounter,
        isNew: addResult.isNew,
        affinityGain: addResult.affinityGain,
        badgeGain: addResult.badgeGain,
        leveledUp: addResult.leveledUp,
        newLevel: addResult.newLevel
      });
    }

    return results.length > 0 ? results : null;
  }

  /**
   * Procesa las recompensas al terminar una guardia.
   * @param {number} xpGained - XP ganado en la sesión.
   * @param {number} correctAnswers - Respuestas correctas en la sesión.
   * @returns {{ xp: number, credits: number }}
   */
    /**
     * Procesa las recompensas al terminar una guardia.
     * @param {Object} stats - Stats de la sesión { xp, correct, total, avgStability }
     * @returns {Object} Recompensas generadas
     */
    processRewards(stats) {
        const { xp, correct, total, avgStability, passiveSystem, perkModifiers, finalReport, isRounds, unpaid, lethals } = stats;
        
        let stabilityVal = avgStability;
        if (finalReport && finalReport.patientState) {
            stabilityVal = finalReport.patientState.estabilidad;
        }

        const grade = this._calculateGrade(correct, total, stabilityVal);

        let rewardXp = Math.round(xp);
        let baseCredits = correct * 50;

        // Apply Cirujano credit bonus: +100 credits for surviving a chain without lethals
        const isCirujano = this.rpg?.classId === 'cirujano';
        let classCreditBonus = 0;
        if (isCirujano && finalReport && finalReport.totalEncounters > 1 && lethals === 0) {
            classCreditBonus = 100;
        }

        let rewardCredits = baseCredits + classCreditBonus;

        // Apply Perk XP bonuses (global multiplier)
        if (perkModifiers?.scoring?.xpGlobalMultiplier > 1) {
            rewardXp = Math.round(rewardXp * perkModifiers.scoring.xpGlobalMultiplier);
        }

        // Apply Passive Bonuses [cite: 5, 12]
        if (passiveSystem) {
          rewardXp = passiveSystem.applyXPBonus(rewardXp);
          rewardCredits = passiveSystem.applyCreditsBonus(rewardCredits);
        }

        let bonusPity = 0;
        let licensesWon = 0;

        // Rewards by Grade [cite: 1, 9]
        if (grade === 'S') { 
            bonusPity = 2; licensesWon = 1; rewardCredits += 100; 
        } else if (grade === 'A') { 
            bonusPity = 1; licensesWon = 1; rewardCredits += 50; 
        }

        // Guardia de estudio (sin energía): el acceso al estudio nunca se bloquea,
        // pero las recompensas económicas se reservan para guardias con energía.
        if (unpaid && !isRounds) {
            rewardXp = Math.round(rewardXp / 2);
            rewardCredits = 0;
            bonusPity = 0;
            licensesWon = 0;
        }

        if (isRounds) {
            rewardXp *= 2; // XP doble por corregir errores en Pase de Visita
            rewardCredits = 0; // El pase de visita no paga créditos
            bonusPity = 0;
            licensesWon = 0;
        }

        this.addPlayerXp(rewardXp);
        this.addCredits(rewardCredits);
        this.pityCounter += bonusPity;
        this.licenses += licensesWon;

        return { xp: rewardXp, credits: rewardCredits, grade, bonusPity, licensesWon, unpaid: !!unpaid };
    }

    _calculateGrade(correct, total, avgStability) {
        const ratio = correct / total;
        if (ratio >= 1.0 && avgStability > 90) return 'S';
        if (ratio >= 0.8 && avgStability > 70) return 'A';
        if (ratio >= 0.6) return 'B';
        if (ratio >= 0.4) return 'C';
        return 'F';
    }

    /**
     * Records a discovered clinical pearl in the Atlas.
     * @returns {boolean} True if it is a new discovery.
     */
    recordPearl(nodeId, nodeData) {
        if (!nodeData || this.discoveredPearls[nodeId]) {
            if (this.discoveredPearls[nodeId]) this.discoveredPearls[nodeId].seenAt = Date.now();
            return false;
        }
        
        // Rarity Mapping [cite: 1, 9]
        let rarity = 'Bronce';
        const rawDifficulty = nodeData.difficulty || nodeData.dificultad || 'easy';
        const difficulty = String(rawDifficulty).toLowerCase();
        if (difficulty === 'hard') rarity = 'Oro';
        else if (difficulty === 'standard') rarity = 'Plata';

        this.discoveredPearls[nodeId] = {
            topic: nodeData.topic || nodeData.tema || 'General',
            pearl: nodeData.text || nodeData.justificacion || 'Sin perla disponible.',
            rarity,
            seenAt: Date.now(),
            error_tipico: nodeData.error_tipico || null
        };


        // Automatic discovery bonus [cite: 5, 12]
        this.addPlayerXp(100); 
        return true;
    }

    /**
     * Returns pearls not seen in > 48 hours for SRS review.
     */
    getForgottenPearls() {
        const now = Date.now();
        const threshold = 48 * 3600 * 1000; // 48 hours
        const forgotten = {};
        for (const [id, data] of Object.entries(this.discoveredPearls)) {
            if (now - data.seenAt > threshold) {
                forgotten[id] = data;
            }
        }
        return forgotten;
    }

    /**
     * Guaranteed SR mentor for the first tutorial pull.
     */
    performTutorialPull() {
        if (this.firstPullDone) return null;
        
        const tutorialMentorId = 'sr_schwerkraftfrei'; // Fixable SR mentor ID from mentors.json
        const mentors = window.MENTORS_DB || [];
        const mentorData = mentors.find(m => m.id === tutorialMentorId) || mentors.find(m => m.rarity === 'SR');

        const item = { id: mentorData.id, rarity: mentorData.rarity, type: "mentor", timestamp: Date.now() };
        this._addToRoster(item);
        this.firstPullDone = true;
        return { success: true, item, isTutorial: true };
    }

    _determineRarity() {
    const hardPity = this.config.pity.hard_pity || 80;
    const softPityStart = Math.floor(hardPity * 0.82); // ~65 for 80 pity

    if (this.pityCounter >= hardPity) return "SSR";

    const baseSSR = this.config.banners[0].pool.SSR;
    let actualSSR = baseSSR;

    // Soft Pity Logic: Exponentially increase rates after 80% of hard pity
    if (this.pityCounter > softPityStart) {
      const delta = this.pityCounter - softPityStart;
      actualSSR = baseSSR + (delta * 0.05); // Increase by 5% per pull
    }

    const rng = Math.random();
    const p = this.config.banners[0].pool;

    if (rng < actualSSR) return "SSR";
    if (rng < actualSSR + p.SR) return "SR";
    if (rng < actualSSR + p.SR + p.R) return "R";
    return "C";
  }

  /**
   * Monte Carlo simulation to verify gacha balance
   */
  gachaAudit(iterations = 1000) {
    let ssrCount = 0;
    let tempPity = 0;
    const originalPity = this.pityCounter;

    for (let i = 0; i < iterations; i++) {
      tempPity++;
      const hardPity = 80;
      const softPityStart = 65;
      let rate = 0.015;
      if (tempPity >= hardPity) {
        ssrCount++; tempPity = 0; continue;
      }
      if (tempPity > softPityStart) rate += (tempPity - softPityStart) * 0.05;
      if (Math.random() < rate) {
        ssrCount++; tempPity = 0;
      }
    }
    logger.debug(`[GachaAudit] SSR Rate: ${(ssrCount / iterations * 100).toFixed(2)}% | Pulls: ${iterations}`);
  }

  _addToRoster(item) {
    if (!this.roster[item.id]) {
      this.roster[item.id] = {
        id: item.id,
        count: 1,
        affinity: 0,
        level: 1,
        rarity: item.rarity,
        type: item.type
      };
      const mentors = window.MENTORS_DB || [];
      const mentorMeta = mentors.find(m => m.id === item.id);
      if (mentorMeta) {
        this.roster[item.id].name = mentorMeta.name;
        this.roster[item.id].title = mentorMeta.title;
        this.roster[item.id].icon = mentorMeta.icon;
      }
      return { isNew: true };
    } else {
      this.roster[item.id].count++;
      // Duplicate rules (Fase 3): +50 affinity and +1 badge
      this.badges = (this.badges || 0) + 1;
      const levelUpInfo = this.addAffinity(item.id, 50);
      return { 
        isNew: false, 
        affinityGain: 50, 
        badgeGain: 1, 
        leveledUp: levelUpInfo.leveledUp, 
        newLevel: levelUpInfo.newLevel 
      };
    }
  }

  addAffinity(mentorId, amount = 10) {
    if (this.roster[mentorId]) {
      this.roster[mentorId].affinity = (this.roster[mentorId].affinity || 0) + amount;
      let leveledUp = false;
      let nextLevelReq = (this.roster[mentorId].level || 1) * 100;
      while (this.roster[mentorId].affinity >= nextLevelReq) {
        this.roster[mentorId].affinity -= nextLevelReq;
        this.roster[mentorId].level = (this.roster[mentorId].level || 1) + 1;
        leveledUp = true;
        nextLevelReq = this.roster[mentorId].level * 100;
      }
      return { leveledUp, newLevel: this.roster[mentorId].level };
    }
    return { leveledUp: false };
  }

  equipMentor(mentorId, slot = 'primary') {
    if (!this.roster[mentorId]) return false;
    
    if (slot === 'primary') {
      this.activeMentor = mentorId;
    } else {
      this.secondaryMentor = mentorId;
    }
    return true;
  }

  buyStamina() {
    if (this.credits < 100) return { success: false, reason: "Créditos insuficientes (cuesta 100 $)" };
    if (this.stamina >= this.maxStamina) return { success: false, reason: "Tu energía ya está al máximo" };
    this.credits -= 100;
    this.stamina++;
    return { success: true, newStamina: this.stamina };
  }

  exchangeMentor(mentorId) {
    if (this.badges < 5) return { success: false, reason: "Insignias insuficientes (cuesta 5 insignias)" };
    const mentors = window.MENTORS_DB || [];
    const mentor = mentors.find(m => m.id === mentorId);
    if (!mentor) return { success: false, reason: "Mentor no encontrado" };
    
    this.badges -= 5;
    const addResult = this._addToRoster({ id: mentor.id, rarity: mentor.rarity, type: "mentor" });
    return { success: true, isNew: addResult.isNew, mentor };
  }

  /**
   * FSRS-Lite Memory Model Update
   * Adjusts topic stability and difficulty based on outcome.
   */
  updateTopicStats(topic, isCorrect) {
    if (!topic) return;
    const key = `__topic_${topic}`;
    const stats = this.caseHistory[key] || { stability: 0, difficulty: 5, seen: 0, correct: 0 };

    stats.seen = (stats.seen || 0) + 1;
    if (isCorrect) {
      stats.correct = (stats.correct || 0) + 1;
      // Increase stability by 1.5x + 1 base
      stats.stability = (stats.stability + 1) * 1.5;
      stats.difficulty = Math.max(1, stats.difficulty - 0.5);
    } else {
      // Slash stability and spike difficulty
      stats.stability = stats.stability * 0.4;
      stats.difficulty = Math.min(10, stats.difficulty + 1.5);
    }

    this.caseHistory[key] = stats;

    if (stats.seen >= 5 && (stats.correct / stats.seen) >= 0.8) {
      this.checkMasteryUnlock(topic);
    }
  }

  checkMasteryUnlock(topic) {
    const mentors = window.MENTORS_DB || [];
    const domainMentor = mentors.find(m => m.specialty === topic);
    if (domainMentor && !this.roster[domainMentor.id]) {
        const res = this.awardSpecificMentor(domainMentor.id);
        if (res.success && res.isNew) {
            console.log(`[EconomySystem] Desbloqueo por dominio: ${domainMentor.name} (${topic})`);
            // We dispatch an event so UI can show a notification.
            window.dispatchEvent(new CustomEvent('mentorUnlockedByMastery', { detail: domainMentor }));
        }
    }
  }

  /**
   * Records a case result to track precision streaks and award Badge of Excellence.
   */
  recordCaseOutcome(isPerfect) {
    if (isPerfect) {
      this.precisionStreak++;
      if (this.precisionStreak >= 50) {
        this.precisionStreak = 0;
        this.excellenceBadges++;
        this.badges += 1; // Award a generic badge too
        return true; // Badge awarded
      }
    } else {
      this.precisionStreak = 0;
    }
    return false;
  }

  _today() {
    return new Date().toISOString().slice(0, 10);
  }

  _deriveLevel(totalXp) {
    let level = 1;
    let remaining = Math.max(0, totalXp);
    let requirement = 50;
    while (remaining >= requirement) {
      remaining -= requirement;
      level += 1;
      requirement = Math.round(requirement * 1.15 + 20);
    }
    return {
      level,
      xpIntoLevel: remaining,
      xpForNextLevel: requirement
    };
  }

  exportState() {
    return {
      credits: this.credits,
      badges: this.badges,
      pityCounter: this.pityCounter,
      dailyPulls: this.dailyPulls,
      lastPullResetDate: this.lastPullResetDate,
      roster: JSON.parse(JSON.stringify(this.roster)),
      stamina: this.stamina,
      last_stamina_update: this.last_stamina_update,
      last_burnout_update: this.last_burnout_update,
      firstGuard: this.firstGuard,
      firstPullDone: this.firstPullDone,
      studyPoints: this.studyPoints,
      caseHistory: JSON.parse(JSON.stringify(this.caseHistory)),
      playerXp: this.playerXp,
      playerLevel: this.playerLevel,
      licenses: this.licenses,
      maxStamina: this.maxStamina,
      xpBonusMult: this.xpBonusMult,
      burnoutResist: this.burnoutResist,
      rpgProfile: this.rpg.exportState(),
      skillPoints: this.rpg.skillPoints, // mirror for UI (perk/talent points)
      burnout: this.burnout,
      activeMentor: this.activeMentor,
      secondaryMentor: this.secondaryMentor,
      precisionStreak: this.precisionStreak,
      excellenceBadges: this.excellenceBadges,
      unlockedAchievements: this.unlockedAchievements,
      dailyGuards: this.dailyGuards,
      lifetimeCleanCases: this.lifetimeCleanCases,
      perfectStabilityGuards: this.perfectStabilityGuards,
      lastGuardDate: this.lastGuardDate,
      discoveredPearls: JSON.parse(JSON.stringify(this.discoveredPearls || {})),
      adaptiveState: this.adaptiveState, // Will be set by main.js before saving
      dayNumber: this.dayNumber,
      guardsToday: this.guardsToday,
      dayLog: JSON.parse(JSON.stringify(this.dayLog || [])),
      storyBeatsSeen: JSON.parse(JSON.stringify(this.storyBeatsSeen || []))
    };
  }

  importState(stateData) {
    if (!stateData) return;
    const fields = [
      'credits', 'badges', 'pityCounter', 'dailyPulls', 'lastPullResetDate',
      'roster', 'stamina', 'last_stamina_update', 'last_burnout_update', 'firstGuard', 'firstPullDone',
      'studyPoints', 'caseHistory', 'playerXp', 'playerLevel', 'licenses',
      'maxStamina', 'xpBonusMult', 'burnoutResist', 'burnout', 'activeMentor',
      'secondaryMentor', 'precisionStreak', 'excellenceBadges', 'unlockedAchievements',
      'dailyGuards', 'lifetimeCleanCases', 'perfectStabilityGuards', 'lastGuardDate',
      'discoveredPearls', 'adaptiveState', 'dayNumber', 'guardsToday', 'dayLog', 'storyBeatsSeen'
    ];
    fields.forEach(f => {
      if (stateData[f] !== undefined) {
        if (typeof stateData[f] === 'object' && stateData[f] !== null) {
          this[f] = JSON.parse(JSON.stringify(stateData[f]));
        } else {
          this[f] = stateData[f];
        }
      }
    });

    if (stateData.rpgProfile) {
      this.rpg = new PlayerProfile(stateData.rpgProfile);
    }
    this.state = this;
  }

  awardAchievement(ach) {
    if (this.unlockedAchievements.includes(ach.id)) return false;
    this.unlockedAchievements.push(ach.id);

    const reward = ach.reward || {};
    switch (reward.type) {
      case 'credits':
        this.addCredits(reward.value);
        break;
      case 'xp':
        this.addPlayerXp(reward.value);
        break;
      case 'xp_bonus':
        // Permanent global XP multiplier (e.g. +5%)
        this.xpBonusMult += reward.value;
        break;
      case 'stamina_max':
        // Raise the stamina cap and grant the new point immediately
        this.maxStamina += reward.value;
        this.stamina = Math.min(this.maxStamina, this.stamina + reward.value);
        break;
      case 'burnout_resist':
        // Permanent burnout mitigation, capped at 90%
        this.burnoutResist = Math.min(0.9, this.burnoutResist + reward.value);
        break;
      default:
        logger.warn(`[Economy] Tipo de recompensa de logro desconocido: ${reward.type}`);
    }
    return true;
  }
}